import { useCallback, useMemo, useRef, useState } from "react";

/**
 * Lista de arquivos com identidade estavel, renomeacao, ordenacao A-Z/Z-A
 * (pt-BR, sem diferenciar maiusculas) e ordem manual por arrastar e soltar.
 *
 * - `entries` guarda SEMPRE a ordem manual (fonte da verdade);
 * - `displayed` e a ordem visivel: entries ordenadas quando A-Z/Z-A ativo;
 * - `uploadSequence()` devolve a sequencia visivel (a que o usuario ve) com
 *   ids estaveis — o backend aplica exatamente essa ordem no processamento.
 */

export const collator = new Intl.Collator("pt-BR", { sensitivity: "base", numeric: true });

export function splitExt(name) {
    const idx = name.lastIndexOf(".");
    if (idx <= 0) return { base: name, ext: "" };
    return { base: name.slice(0, idx), ext: name.slice(idx) };
}

const INVALID_CHARS = /[\\/:*?"<>|\x00-\x1f]/;

export function validateBaseName(base) {
    const trimmed = (base || "").trim();
    if (!trimmed) return "O nome não pode ficar vazio.";
    if (INVALID_CHARS.test(trimmed)) return 'O nome não pode conter \\ / : * ? " < > |';
    if (trimmed.length > 120) return "Nome muito longo (máx. 120 caracteres).";
    return null;
}

let counter = 0;
function newFileId() {
    counter += 1;
    return (crypto.randomUUID && crypto.randomUUID()) || `f-${Date.now()}-${counter}`;
}

export function useFileEntries({ maxFiles, maxFileMB, multi }) {
    const [entries, setEntries] = useState([]);
    const [sortMode, setSortMode] = useState("manual"); // manual | asc | desc
    const [listError, setListError] = useState(null);
    const seqRef = useRef(0);

    const addFiles = useCallback(
        (list) => {
            setListError(null);
            const incoming = Array.from(list || []);
            if (!incoming.length) return;
            const accepted = [];
            for (const f of incoming) {
                if (f.size > maxFileMB * 1024 * 1024) {
                    setListError(`"${f.name}" excede o limite de ${maxFileMB} MB do seu plano.`);
                    continue;
                }
                seqRef.current += 1;
                accepted.push({
                    id: newFileId(),
                    seq: seqRef.current, // ordem original de carregamento
                    file: f,
                    name: f.name,
                    size: f.size,
                    type: f.type,
                });
            }
            setEntries((prev) => {
                const merged = multi ? [...prev, ...accepted] : accepted.slice(-1);
                return merged.slice(0, maxFiles);
            });
        },
        [maxFiles, maxFileMB, multi]
    );

    const remove = useCallback((id) => {
        setEntries((prev) => prev.filter((e) => e.id !== id));
    }, []);

    /** Renomeia apenas o nome de exibicao; extensao original preservada. */
    const rename = useCallback((id, newBase) => {
        const err = validateBaseName(newBase);
        if (err) return err;
        setEntries((prev) =>
            prev.map((e) => {
                if (e.id !== id) return e;
                const { ext } = splitExt(e.name);
                return { ...e, name: newBase.trim() + ext };
            })
        );
        return null;
    }, []);

    /** Aplica ordem manual vinda do drag-and-drop (array de ids). */
    const reorder = useCallback((orderedIds) => {
        setEntries((prev) => {
            const byId = new Map(prev.map((e) => [e.id, e]));
            const next = orderedIds.map((id) => byId.get(id)).filter(Boolean);
            if (next.length !== prev.length) return prev; // nunca desfaz a ordem por renderizacao
            return next;
        });
        setSortMode("manual");
    }, []);

    /** Restaura a ordem original de carregamento. */
    const restoreOriginal = useCallback(() => {
        setEntries((prev) => [...prev].sort((a, b) => a.seq - b.seq));
        setSortMode("manual");
    }, []);

    const reset = useCallback(() => {
        setEntries([]);
        setSortMode("manual");
        setListError(null);
    }, []);

    const displayed = useMemo(() => {
        if (sortMode === "asc") {
            return [...entries].sort((a, b) => collator.compare(a.name, b.name) || collator.compare(a.id, b.id));
        }
        if (sortMode === "desc") {
            return [...entries].sort((a, b) => collator.compare(b.name, a.name) || collator.compare(a.id, b.id));
        }
        return entries;
    }, [entries, sortMode]);

    /** Sequencia visivel (a que o usuario ve) para enviar ao backend. */
    const uploadSequence = useCallback(() => {
        return {
            files: displayed.map((e) => e.file),
            ids: displayed.map((e) => e.id),
            names: displayed.map((e) => e.name),
        };
    }, [displayed]);

    return {
        entries,
        displayed,
        sortMode,
        setSortMode,
        listError,
        setListError,
        addFiles,
        remove,
        rename,
        reorder,
        restoreOriginal,
        reset,
        uploadSequence,
    };
}
